import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { gsap } from 'gsap'
import './HeaderHome.scss'

const HeaderHome = ({ showSlider, onCloseSlider, headerColor = 'white', backgroundColor = '#ffffff', buttons = null }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const btn = buttons?.fields || {};
    const [isBiographyVisible, setIsBiographyVisible] = useState(false);
    const [isHeroInView, setIsHeroInView] = useState(true);
    const [isHighlightsInView, setIsHighlightsInView] = useState(false);
    const overlayRef = useRef(null);
    const menuRef = useRef(null);
    const alexTextRef = useRef(null);
    const biographyTextRef = useRef(null);
    const dashRef = useRef(null);
    const prevBioVisibleRef = useRef(null);



    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const scrollToBiography = () => {
        const biographySection = document.querySelector('.home-info-section');
        if (biographySection) {
            biographySection.scrollIntoView({ behavior: 'smooth' });
        }
        setIsMenuOpen(false); // Close menu after clicking
    };

    // Intersection Observer for hero section (use viewport root)
    useEffect(() => {
        const heroSection = document.querySelector('.section-home-hero');
        //console.log('Setting up hero intersection observer');
        //console.log('Hero section found:', !!heroSection);

        if (heroSection) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        // Compute a robust visibility check suitable for mobile scroll containers
                        const rect = entry.target.getBoundingClientRect();
                        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                        const clampedTop = Math.max(rect.top, 0);
                        const clampedBottom = Math.min(rect.bottom, viewportHeight);
                        const visiblePx = Math.max(0, clampedBottom - clampedTop);
                        const denom = Math.max(1, Math.min(viewportHeight, rect.height || viewportHeight));
                        const visibleRatio = visiblePx / denom;

                        const heroInView = entry.isIntersecting && visibleRatio > 0.05; // at least 5% visible
                        setIsHeroInView(heroInView);
                    });
                },
                {
                    threshold: [0, 0.01, 0.1, 0.5, 1],
                    root: null, // use viewport
                    rootMargin: '0px 0px 0px 0px'
                }
            );

            observer.observe(heroSection);

            return () => {
                observer.disconnect();
            };
        }
    }, []);

    // Fallback: recompute hero visibility on window scroll/resize
    useEffect(() => {
        const heroSection = document.querySelector('.section-home-hero');
        if (!heroSection) return;

        const recomputeHeroInView = () => {
            const rect = heroSection.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const visiblePx = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
            const denom = Math.max(1, Math.min(vh, rect.height || vh));
            const ratio = visiblePx / denom;
            setIsHeroInView(ratio > 0.05); // treat as in view if >=5% visible
        };

        // Initial compute
        recomputeHeroInView();

        window.addEventListener('scroll', recomputeHeroInView, { passive: true });
        window.addEventListener('resize', recomputeHeroInView);

        return () => {
            window.removeEventListener('scroll', recomputeHeroInView);
            window.removeEventListener('resize', recomputeHeroInView);
        };
    }, []);

    // Intersection Observer for biography section (use viewport root)
    useEffect(() => {
        const biographySection = document.querySelector('.home-info-section');

        if (biographySection) {
            const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        // Check if the top of the biography section is near the top of the viewport
                        const rect = entry.target.getBoundingClientRect();
                        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

                        // On mobile: section should be mostly visible and scrolled into view
                        // On desktop: use higher threshold
                        const ratioThreshold = isSmallScreen ? 0.3 : 0.6;
                        const isIntersecting = entry.intersectionRatio >= ratioThreshold;

                        // Additional check: the section top should be in the upper portion of viewport
                        const isTopInView = rect.top < (viewportHeight * 0.6);

                        const shouldBeVisible = isIntersecting && isTopInView;

                        // Only update when the visibility state actually changes to avoid rapid re-tweens (Safari flicker)
                        if (prevBioVisibleRef.current !== shouldBeVisible) {
                            prevBioVisibleRef.current = shouldBeVisible;
                            setIsBiographyVisible(shouldBeVisible);

                            gsap.to(entry.target, {
                                backgroundColor: shouldBeVisible ? (backgroundColor || '#ffffff') : 'transparent',
                                duration: 0.25,
                                ease: shouldBeVisible ? 'power2.out' : 'power2.in',
                                overwrite: 'auto',
                                force3D: true,
                                immediateRender: false,
                            });
                        }
                    });
                },
                {
                    root: null, // use viewport
                    // Fewer thresholds to reduce callback churn on Safari
                    threshold: isSmallScreen ? [0, 0.3, 0.6, 1] : [0, 0.4, 0.6, 1],
                    rootMargin: '-10% 0px -10% 0px', // Less restrictive margin
                }
            );

            observer.observe(biographySection);

            return () => {
                observer.disconnect();
            };
        }
    }, [backgroundColor]);

    // Intersection Observer for highlights section
    useEffect(() => {
        // Add a small delay to ensure DOM is fully loaded
        const timer = setTimeout(() => {
            const highlightsSection = document.querySelector('.section-home-highlights');

            if (highlightsSection) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
                            setIsHighlightsInView(isVisible);
                        });
                    },
                    {
                        root: null, // use viewport
                        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
                        rootMargin: '0px 0px 0px 0px',
                    }
                );

                observer.observe(highlightsSection);

                return () => {
                    observer.disconnect();
                };
            }
        }, 100); // 100ms delay

        return () => clearTimeout(timer);
    }, []);

    // Initialize overlay and menu as hidden
    useEffect(() => {
        if (overlayRef.current && menuRef.current) {
            gsap.set(overlayRef.current, { autoAlpha: 0 });
            /*  gsap.set(menuRef.current, { autoAlpha: 0, y: -20 }); */
            gsap.set(menuRef.current, { autoAlpha: 0, y: 0 });
        }

        // Set initial colors to white (hero section is initially in view)
        gsap.set(".header-menu-home", { color: "white" });
        gsap.set(alexTextRef.current, { color: "white" });
    }, []);

    // Handle header color changes based on hero section visibility
    useEffect(() => {
        // Change color based on hero section visibility
        const targetColor = isHeroInView ? "white" : "black";

        gsap.to(".header-menu-home", {
            color: targetColor,
            duration: 0.3,
            ease: "power2.out"
        });

        gsap.to(alexTextRef.current, {
            color: targetColor,
            duration: 0.3,
            ease: "power2.out"
        });

        // Also change biography text and dash color
        if (biographyTextRef.current && dashRef.current) {
            gsap.to([biographyTextRef.current, dashRef.current], {
                color: targetColor,
                duration: 0.3,
                ease: "power2.out"
            });
        }
    }, [isBiographyVisible, isHeroInView]);

    // GSAP animations for biography text visibility
    // Removed to keep the label always visible
    // useEffect(() => {
    //     if (biographyTextRef.current && dashRef.current) {
    //         if (isBiographyVisible) {
    //             gsap.to([biographyTextRef.current, dashRef.current],
    //                 { autoAlpha: 1, duration: 0.2, ease: "power2.out" }
    //             );
    //         } else {
    //             gsap.to([biographyTextRef.current, dashRef.current],
    //                 { autoAlpha: 0, duration: 0.3, ease: "power2.in" }
    //             );
    //         }
    //     }
    // }, [isBiographyVisible]);

    // GSAP animations for title (Biography/overview) visibility
    // Removed to keep the label always visible
    // useEffect(() => {
    //     const shouldShow = isHighlightsInView || isBiographyVisible;
    //     if (biographyTextRef.current && dashRef.current) {
    //         gsap.to([biographyTextRef.current, dashRef.current], {
    //             autoAlpha: shouldShow ? 1 : 0,
    //             duration: 0.2,
    //             ease: shouldShow ? "power2.out" : "power2.in"
    //         });
    //     }
    // }, [isHighlightsInView, isBiographyVisible]);

    // GSAP animations for menu overlay
    useEffect(() => {
        if (isMenuOpen) {
            // Fade in overlay
            gsap.fromTo(overlayRef.current,
                { autoAlpha: 0 },
                { autoAlpha: 1, duration: 0.3, ease: "power2.out" }
            );

            // Fade in menu items
            gsap.fromTo(menuRef.current,
                { autoAlpha: 0, y: -20 },
                { autoAlpha: 1, y: 0, duration: 0.25, ease: "power2.out", delay: 0 }
            );

            // Make Alex de Brabant text white
            gsap.to(alexTextRef.current,
                { color: "white", duration: 0.2, ease: "power2.out" }
            );

            // Make header menu text white
            gsap.to(".header-menu-home",
                { color: "white", duration: 0.2, ease: "power2.out" }
            );

            // Hide biography text, dash, and menu when menu is open
            gsap.to([biographyTextRef.current, dashRef.current, ".header-menu-home"],
                { autoAlpha: 0, duration: 0.1, ease: "power2.in" }
            );

            // No need to hide overview text since it's now part of Biography text

        } else {
            // Fade out overlay
            gsap.to(overlayRef.current,
                { autoAlpha: 0, duration: 0.2, ease: "power2.in" }
            );

            // Fade out menu items
            gsap.to(menuRef.current,
                { autoAlpha: 0, y: -20, duration: 0.2, ease: "power2.in" }
            );

            // Restore Alex de Brabant text color based on hero visibility
            const targetColor = isHeroInView ? "white" : "black";
            gsap.to(alexTextRef.current,
                { color: targetColor, duration: 0.2, ease: "power2.out" }
            );

            // Restore header menu text color based on hero visibility
            gsap.to(".header-menu-home",
                { color: targetColor, duration: 0.2, ease: "power2.out" }
            );

            // Show/hide label (page-title + dash) based on section visibility and hero not in view
            const shouldShowLabel = !isHeroInView && (isHighlightsInView || isBiographyVisible);
            gsap.to([biographyTextRef.current, dashRef.current],
                { autoAlpha: shouldShowLabel ? 1 : 0, duration: 0.2, ease: "power2.out" }
            );
            // Always show menu when menu is closed
            gsap.to(".header-menu-home",
                { autoAlpha: 1, duration: 0.2, ease: "power2.out" }
            );

            // No need to show overview text since it's now part of Biography text
        }
    }, [isMenuOpen, isBiographyVisible, isHeroInView, isHighlightsInView]);

    return (
        <>
            <div className="imprint-home" style={{ visibility: isMenuOpen ? 'visible' : 'hidden' }}>
                <Link to="/imprint">Imprint</Link>
                {/*   <p>Privacy Policy</p> */}
            </div>

            <div className="menu-overlay" ref={overlayRef} onClick={toggleMenu}></div>
            <header className="header-home" >

                <p className="header-alex-home" ref={alexTextRef}>Alex de Brabant</p>
                <div className="menu-container">
                    <div className="menu-container-right">
                        <p className="page-title" ref={biographyTextRef}>
                            {!isHeroInView ? (isHighlightsInView ? 'Overview' : (isBiographyVisible ? 'Biography' : '')) : ''}
                        </p>
                        <p ref={dashRef} className="dash" style={{ visibility: (!isHeroInView && (isHighlightsInView || isBiographyVisible)) ? 'visible' : 'hidden' }}>•</p>
                        <p className={`header-menu-home ${isHeroInView ? 'hero-in-view' : ''}`} onClick={toggleMenu}>Menu</p>
                    </div>
                    <div className="vertical-menu" ref={menuRef} style={{ visibility: isMenuOpen ? 'visible' : 'hidden' }}>
                        {btn.artworks !== false && <Link to="/artworks" className="menu-item">Artworks</Link>}
                        {btn.portraits !== false && <Link to="/portraits" className="menu-item">Portraits</Link>}
                        {btn.commissions !== false && <Link to="/commissions" className="menu-item">Commissions</Link>}
                        {btn.events === true && <Link to="/events" className="menu-item">Events</Link>}
                        <p className="menu-item" onClick={scrollToBiography}>Biography</p>
                        {btn.shop === true && (
                            <a
                                href="https://www.alexdebrabanteditions.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="menu-item"
                            >
                                Shop
                            </a>
                        )}
                        <Link to="/info" className="menu-item">Info</Link>
                    </div>
                </div>
            </header>
        </>
    )
}

export default HeaderHome;